import { useState, useEffect } from 'react'
import PageNavigation from '../../../components/PageNavigation/PageNavigation'
import './APIKeys.css'
import apikey1 from '../../../assets/apikey1.png'

const APIKeys = () => {
  const [activeSection, setActiveSection] = useState('overview')
  const [lightboxImage, setLightboxImage] = useState(null)
  const [lightboxAlt, setLightboxAlt] = useState('')

  const openLightbox = (imageSrc, altText) => {
    setLightboxImage(imageSrc)
    setLightboxAlt(altText)
    document.body.style.overflow = 'hidden'
  }

  const closeLightbox = () => {
    setLightboxImage(null)
    setLightboxAlt('')
    document.body.style.overflow = ''
  }

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && lightboxImage) {
        closeLightbox()
      }
    }
    if (lightboxImage) {
      window.addEventListener('keydown', handleEscape)
    }
    return () => {
      window.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = ''
    }
  }, [lightboxImage])

  const sections = [
    { id: 'overview', title: 'Overview' },
    { id: 'creating', title: 'Creating API Keys' },
    { id: 'using', title: 'Using API Keys' },
    { id: 'management', title: 'Key Management' }
  ]

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 200

      for (let i = sections.length - 1; i >= 0; i--) {
        const element = document.getElementById(`apikeys-${sections[i].id}`)
        if (element && element.offsetTop <= scrollPosition) {
          setActiveSection(sections[i].id)
          break
        }
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()
    return () => window.removeEventListener('scroll', handleScroll)
  }, [sections])

  return (
    <div className="api-keys-page">
      <div className="api-keys-content-wrapper">
        <div className="api-keys-main-content">
      <h1>API Keys</h1>
      <p className="intro">
            Most actions performed in the Aedify.AI Console can also be performed using the <strong>Aedify.AI API</strong>. 
            You'll need an API key to validate your requests. Each key is a randomly-generated token that you must include 
            when calling Aedify.AI API methods. All keys remain valid until deliberately revoked.
      </p>

          <section id="apikeys-overview" className="api-keys-section">
        <h2>Overview</h2>
        <p>
          API keys provide secure access to Aedify.AI's REST API. Use them to manage resources, 
              deploy applications, and interact with services programmatically. API keys authenticate 
              your requests and allow you to integrate Aedify.AI functionality into your applications, 
              CI/CD pipelines, and automation scripts.
        </p>
      </section>

          <section id="apikeys-creating" className="api-keys-section">
        <h2>Creating API Keys</h2>
            <p>
              You can create API keys from the Aedify.AI Console. Navigate to <strong>Dashboard &gt; API Keys </strong> 
              to access the API key management interface. Check out the image below for a list of existing keys and the button to create a new key.
            </p>
            
            <div className="step-image">
              <img 
                src={apikey1} 
                alt="API Keys management interface showing the list of API keys"
                onClick={() => openLightbox(apikey1, "API Keys management interface showing the list of API keys")}
                className="clickable-image"
              />
            </div>

            <div className="important-warning">
              <p>
                The secret token is displayed only once when you create the API key. You must copy it immediately 
                and store it securely in a credential manager (e.g., AWS Key Management Service, Azure Key Vault, 
                or a password manager). Lost API keys must be revoked and re-created.
              </p>
            </div>

            <div className="important-notice">
              <div className="notice-header">
                <span className="notice-label">IMPORTANT</span>
              </div>
              <p>
                You are responsible for maintaining the records and associations of any API keys in your 
                environment and systems.
        </p>
            </div>
      </section>

          <section id="apikeys-using" className="api-keys-section">
        <h2>Using API Keys</h2>
        <p>
          Include your API key in the Authorization header of API requests:
        </p>
        <pre><code>{`curl -X GET https://api.aedify.ai/v1/projects \\
  -H "Authorization: Bearer YOUR_API_KEY"`}</code></pre>
        <h3>JavaScript Example</h3>
        <pre><code>{`const response = await fetch('https://api.aedify.ai/v1/projects', {
  headers: {
    'Authorization': 'Bearer YOUR_API_KEY',
    'Content-Type': 'application/json'
  }
});`}</code></pre>
      </section>

          <section id="apikeys-management" className="api-keys-section">
            <h2>Key Management</h2>
            <h3>Key Expiration</h3>
        <p>
              Set expiration dates for API keys to ensure they're rotated regularly. Expired keys 
              automatically stop working. You can choose from the following expiration options:
        </p>
        <ul>
              <li><strong>30 days</strong></li>
              <li><strong>60 days</strong></li>
              <li><strong>90 days</strong></li>
              <li><strong>180 days</strong></li>
              <li><strong>1 year</strong></li>
              <li><strong>Never</strong> - The key will remain valid until manually revoked</li>
        </ul>
            <p>
              When creating or editing an API key, select your preferred expiration time from the dropdown menu. 
              This helps maintain security by ensuring keys are rotated periodically.
            </p>

        <h3>Rotating Keys</h3>
        <p>
          Regularly rotate your API keys for security. Create a new key, update your applications, 
          then revoke the old key.
        </p>
            
            <h3>Revoke API Keys</h3>
        <p>
              You should revoke API keys that are no longer needed or if you suspect a key may have been compromised. 
              Key details:
        </p>
            <ul>
              <li>The action is immediate and permanent</li>
              <li>All API requests using the revoked key will fail with a <strong>401 Unauthorized</strong> error</li>
              <li>The key cannot be reactivated — you'll need to create a new key if access is needed again</li>
            </ul>
      </section>

          <PageNavigation />
        </div>

        {/* Right Sidebar - Section Navigation */}
        <aside className="api-keys-sidebar">
          <div className="sidebar-section">
            <h3>Contents</h3>
            <nav className="section-nav">
              {sections.map((section) => (
                <a
                  key={section.id}
                  href={`#apikeys-${section.id}`}
                  className={`section-nav-item ${activeSection === section.id ? 'active' : ''}`}
                  onClick={(e) => {
                    e.preventDefault()
                    setActiveSection(section.id)
                    document.getElementById(`apikeys-${section.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                  }}
                >
                  {section.title}
                </a>
              ))}
            </nav>
          </div>

          <div className="sidebar-section tips-section">
            <h3>Tips</h3>
            <ul className="tips-list">
          <li>Use different keys for different applications or environments</li>
          <li>Never commit API keys to version control</li>
          <li>Use environment variables to store keys</li>
          <li>Rotate keys regularly</li>
          <li>Use the minimum permissions necessary</li>
        </ul>
          </div>
        </aside>
      </div>

      {/* Lightbox Modal */}
      {lightboxImage && (
        <div className="lightbox-overlay" onClick={closeLightbox}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={closeLightbox} aria-label="Close lightbox">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <img src={lightboxImage} alt={lightboxAlt} className="lightbox-image" />
          </div>
        </div>
      )}
    </div>
  )
}

export default APIKeys
